// src/auth/AuthContext.js
import {
  createContext,
  useContext,
  useEffect,
  useState
} from 'react';

import { logAuthMe } from './logAuth';  // ok if present; it's optional below

const DEBUG = process.env.NODE_ENV !== 'production';

const SUPER_USERS = [
  'hstaggs@bmss.com',
  'tcrawford@bmss.com',
  'deison@bmss.com',
  'dbrock@bmss.com',
  'csibley@bmss.com'
];

// Dev-team members who can triage bug reports (the dev UI). Distinct from
// SUPER_USERS (Production Committee) — these are the developers. Must stay a
// subset of func-shared-services ADMIN_EMAILS (the backend boundary that
// actually authorizes reading-all + resolving), else triage calls 403.
export const DEV_SUPERUSERS = [
  'hstaggs@bmss.com',
  'deison@bmss.com',
  'tcrawford@bmss.com',
  'rwiginton@bmss.com'
];

// SWA intermittently masks userDetails (e.g. "tma*****@bmss.com"). The
// principal.userId (AAD object ID) survives masking and is stable across
// masked + unmasked sessions for the same user. We cache userId → email on
// healthy auths and read from the cache when masked. See plan / AuthLog
// instrumentation for the evidence behind this.
const CACHE_KEY = 'rprs.auth.userIdEmailCache';
const readUserIdCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); } catch { return {}; }
};
const writeUserIdCache = (obj) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(obj)); } catch { /* private-browsing / quota — ignore */ }
};

// ── Sign-in redirect (shared) ───────────────────────────────────────────────
// Hoisted out of AuthProvider's mount effect so the session-expiry path can
// reuse the exact same logic. Two callers today:
//   • AuthProvider below — cold load where /.auth/me comes back anonymous
//   • components/SessionExpiredModal — a live session that died mid-use
// Keep them sharing this; a second private copy is how the app ended up with
// two divergent postWithRetry helpers (BugReporter / SuggestionReporter).

// Local dev runs behind the SWA CLI emulator, which owns auth itself — there's
// no edge cookie to expire and no AAD flow to run, so we never force a redirect.
export function isLocalDev() {
  return typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
     window.location.hostname.startsWith('127.') ||
     window.location.hostname.endsWith('.local'));
}

// Send the browser to AAD sign-in, returning to the current URL afterward.
// No-op in local dev.
export function redirectToAAD() {
  if (isLocalDev()) return;
  const here = window.location.pathname + window.location.search + window.location.hash;
  const loginUrl = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(here);
  window.location.assign(loginUrl);
}

const AuthCtx = createContext({
  ready         : false,
  principal     : null,
  isSuperUser   : false,
  isDevSuperUser: false,
  email         : undefined,
  username      : undefined,
  blocked       : false,
});

export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    ready         : false,
    principal     : null,
    isSuperUser   : false,
    isDevSuperUser: false,
    email         : undefined,
    username      : undefined,
    blocked       : false,
  });

  useEffect(() => {
    // Optional debug probe if the helper exists in this repo
    try { DEBUG && typeof logAuthMe === 'function' && logAuthMe(); } catch {}

    // local dev? (don’t force redirect) — isLocalDev/redirectToAAD are module
    // scope now so SessionExpiredModal can reuse them; see top of file.
    const isLocal = isLocalDev();

    const url = '/.auth/me';
    if (DEBUG) console.groupCollapsed('%cAUTH fetch', 'color:#0af', url);

    fetch(url, { credentials: 'include', cache: 'no-store' })
      .then(async r => {
        if (DEBUG) {
          console.debug('status     ', r.status, r.statusText);
          console.debug('redirected ', r.redirected);
          console.debug('responseURL', r.url);
          console.debug('contentType', r.headers.get('content-type'));
        }
        const json = await r.json().catch(() => ({}));
        if (DEBUG) console.debug('body       ', json);
        return json;
      })
      .then(({ clientPrincipal }) => {
        // anonymous in prod → force O365 sign-in
        if (!clientPrincipal && !isLocal) {
          redirectToAAD();
          return;
        }

        let email = clientPrincipal?.userDetails?.toLowerCase() || '';
        const uid = clientPrincipal?.userId || null;

        // SWA sometimes masks userDetails with asterisks (e.g., "sta*****@bmss.com").
        // Fall back to claims array which has the unmasked email.
        // (Step 1 instrumentation showed claims arrive as [] on masked sessions,
        // but we keep the fallback for defense-in-depth in case SWA changes.)
        if (email.includes('*')) {
          console.warn('[AUTH] Masked email detected:', email);
          if (clientPrincipal?.claims) {
            const emailClaim = clientPrincipal.claims.find(c =>
              c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress' ||
              c.typ === 'preferred_username' || c.typ === 'email'
            );
            if (emailClaim?.val) {
              console.info('[AUTH] Resolved from claims:', emailClaim.val);
              email = emailClaim.val.toLowerCase();
            } else {
              console.warn('[AUTH] No email claim found. Claims:', clientPrincipal.claims?.map(c => c.typ));
            }
          }
        }

        // UserId-keyed cache: when still masked after the claim fallback, look up
        // the cached real email for this AAD object ID. When auth is healthy,
        // refresh the cache so future masked sessions can recover.
        if (email.includes('*') && uid) {
          const cached = readUserIdCache()[uid];
          if (cached && !cached.includes('*') && cached.endsWith('@bmss.com')) {
            console.info('[AUTH] Resolved masked email from UserId cache:', cached);
            email = cached;
          }
        } else if (uid && !email.includes('*') && email.endsWith('@bmss.com')) {
          const cache = readUserIdCache();
          if (cache[uid] !== email) {
            cache[uid] = email;
            writeUserIdCache(cache);
          }
        }

        // Normalize: SWA may return just username (no @domain) for some AAD configs
        if (email && !email.includes('@')) {
          email = email + '@bmss.com';
        }

        // Domain restriction: only @bmss.com users allowed
        if (clientPrincipal && email && !email.endsWith('@bmss.com')) {
          console.warn('[AUTH] Unauthorized domain:', email);
          setState({
            ready: true, principal: clientPrincipal, isSuperUser: false, isDevSuperUser: false,
            email, username: email.split('@')[0] || '', blocked: true,
          });
          return;
        }

        const isSuper = SUPER_USERS.includes(email);
        const isDev = DEV_SUPERUSERS.includes(email);

        if (DEBUG) {
          console.debug('principal  ', {
            email,
            identityProvider: clientPrincipal?.identityProvider,
            userId: clientPrincipal?.userId,
            userRoles: clientPrincipal?.userRoles
          });
          if (!email && !isLocal) {
            console.warn('[AUTH] Empty email from /.auth/me — likely anonymous session.');
          }
        }

        const username = email.split('@')[0] || '';
        setState({
          ready         : true,
          principal     : clientPrincipal ?? null,
          isSuperUser   : isSuper,
          isDevSuperUser: isDev,
          email,
          username
        });
      })
      .catch(err => {
        console.error('AUTH error', err);
        setState({ ready: true, principal: null, isSuperUser: false, isDevSuperUser: false, email: '', username: '' });
      })
      .finally(() => { if (DEBUG) console.groupEnd?.(); });
  }, []);

  // Access denied page for non-BMSS users
  if (state.blocked) {
    return (
      <AuthCtx.Provider value={state}>
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, #003D4C 0%, #356371 100%)', fontFamily: 'Arial, sans-serif',
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '48px 40px', maxWidth: 460, width: '90%',
            textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#128274;</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#003D4C', margin: '0 0 8px' }}>Access Denied</h1>
            <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, margin: '0 0 20px' }}>
              This application is restricted to <strong>@bmss.com</strong> accounts.
              You are signed in as <strong style={{ color: '#DC2626' }}>{state.email}</strong>, which is not authorized.
            </p>
            <p style={{ fontSize: 13, color: '#9CA3AF', margin: '0 0 24px' }}>
              If you believe this is an error or need access, please contact:
            </p>
            <a href="mailto:dataanalyticsteam@bmss.com"
              style={{
                display: 'inline-block', background: '#003D4C', color: '#fff', padding: '10px 24px',
                borderRadius: 8, fontSize: 14, fontWeight: 700, textDecoration: 'none',
              }}>
              dataanalyticsteam@bmss.com
            </a>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #E5E7EB' }}>
              <a href="/.auth/logout" style={{ fontSize: 12, color: '#6B7280', textDecoration: 'none' }}>
                Sign out and try a different account &rarr;
              </a>
            </div>
          </div>
        </div>
      </AuthCtx.Provider>
    );
  }

  return (
    <AuthCtx.Provider value={state}>
      {children}
    </AuthCtx.Provider>
  );
}